import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import MfaDialog from "@/components/MfaDialog";
import { getDeviceFingerprint } from "@/components/MfaDialog";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextValue>({ user: null, loading: true });

export const useAuth = () => useContext(AuthContext);

const ADMIN_INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes
const ACTIVITY_KEY = "uc_admin_last_active";

function isDeviceTrusted(userId: string): boolean {
  try {
    const trusted = JSON.parse(localStorage.getItem("uc_trusted_devices") || "[]");
    const fp = getDeviceFingerprint();
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    return trusted.some(
      (d: { userId: string; fingerprint: string; trustedAt: number }) =>
        d.userId === userId && d.fingerprint === fp && Date.now() - d.trustedAt < thirtyDays
    );
  } catch {
    return false;
  }
}

/** Returns true if admin was active within the last 30 minutes */
function isAdminSessionFresh(): boolean {
  try {
    const last = Number(localStorage.getItem(ACTIVITY_KEY) || "0");
    return Date.now() - last < ADMIN_INACTIVITY_MS;
  } catch {
    return false;
  }
}

function touchAdminActivity() {
  localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaUserId, setMfaUserId] = useState("");
  const [mfaMaskedEmail, setMfaMaskedEmail] = useState("");
  const [pendingUser, setPendingUser] = useState<User | null>(null);
  const mfaPendingRef = useRef(false);
  const isAdminRef = useRef(false);

  const triggerMfa = useCallback((sessionUser: User) => {
    const email = sessionUser.email || "";
    const masked = email.replace(/(.{2})(.*)(@.*)/, "$1***$3");
    setPendingUser(sessionUser);
    setMfaUserId(sessionUser.id);
    setMfaMaskedEmail(masked);
    setMfaPending(true);
    mfaPendingRef.current = true;
    setLoading(false);
  }, []);

  const checkMfa = useCallback(async (sessionUser: User) => {
    const { data: profileData } = await supabase
      .from("profiles")
      .select("mfa_enabled")
      .eq("id", sessionUser.id)
      .maybeSingle();

    const { data: isAdmin } = await supabase.rpc("has_role", {
      _user_id: sessionUser.id,
      _role: "admin",
    });

    isAdminRef.current = !!isAdmin;

    const userMfaEnabled = profileData?.mfa_enabled === true;
    const trustedDevice = isDeviceTrusted(sessionUser.id);

    // Admin path: use 30-min activity window instead of per-login MFA
    if (isAdmin && userMfaEnabled) {
      if (isAdminSessionFresh()) {
        // Still within 30-min window — skip MFA, refresh timestamp
        touchAdminActivity();
        setUser(sessionUser);
        setLoading(false);
        return;
      }
      // Inactive too long — require MFA
      triggerMfa(sessionUser);
      return;
    }

    // Non-admin path: use device trust (30-day)
    if (!userMfaEnabled || trustedDevice) {
      setUser(sessionUser);
      setLoading(false);
      return;
    }

    // MFA required
    triggerMfa(sessionUser);
  }, [triggerMfa]);

  // Track admin activity — mouse, keyboard, touch
  useEffect(() => {
    if (!user || !isAdminRef.current) return;

    const onActivity = () => touchAdminActivity();
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    // Initial touch
    touchAdminActivity();

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [user]);

  // Visibility change — lock admin after 30 min away
  useEffect(() => {
    if (!user || !isAdminRef.current) return;

    const onVisChange = () => {
      if (document.visibilityState === "visible" && !isAdminSessionFresh()) {
        // Session expired while tab was hidden — require MFA
        setUser(null);
        triggerMfa(user);
      } else if (document.visibilityState === "visible") {
        touchAdminActivity();
      }
    };

    document.addEventListener("visibilitychange", onVisChange);
    return () => document.removeEventListener("visibilitychange", onVisChange);
  }, [user, triggerMfa]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session?.user) {
          if (_event === "SIGNED_IN") {
            checkMfa(session.user);
          } else {
            if (!mfaPendingRef.current) {
              setUser(session.user);
            }
          }
        } else {
          setUser(null);
          setPendingUser(null);
          setMfaPending(false);
          isAdminRef.current = false;
        }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        if (isDeviceTrusted(session.user.id)) {
          // Check admin freshness on reload
          supabase.rpc("has_role", { _user_id: session.user.id, _role: "admin" }).then(({ data: isAdmin }) => {
            isAdminRef.current = !!isAdmin;
            if (isAdmin && !isAdminSessionFresh()) {
              checkMfa(session.user);
            } else {
              if (isAdmin) touchAdminActivity();
              setUser(session.user);
              setLoading(false);
            }
          });
        } else {
          checkMfa(session.user);
        }
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkMfa]);

  const handleMfaVerified = () => {
    setMfaPending(false);
    mfaPendingRef.current = false;
    // Mark admin session as fresh
    if (isAdminRef.current) {
      touchAdminActivity();
    }
    setUser(pendingUser);
    setPendingUser(null);
  };

  const handleMfaCancel = async () => {
    setMfaPending(false);
    mfaPendingRef.current = false;
    setPendingUser(null);
    setUser(null);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
      <MfaDialog
        open={mfaPending}
        userId={mfaUserId}
        maskedEmail={mfaMaskedEmail}
        onVerified={handleMfaVerified}
        onCancel={handleMfaCancel}
      />
    </AuthContext.Provider>
  );
};
